import { DeviceType, type Wallpaper, type Resolution } from '../firebase/schema'

const getBestResolution = (resolutions: Resolution[] = []) => {
  if (!resolutions || resolutions.length === 0) return null
  // Prefer the highest resolution available
  return resolutions.reduce((best, r) => (r.width * r.height > (best?.width || 0) * (best?.height || 0) ? r : best))
}

// Runtime aspect-ratio cache (filled from image natural sizes in the browser)
const aspectCache = new Map<string, number>()

export const warmAspectCache = async (wallpapers: Wallpaper[], limit = 40): Promise<void> => {
  const slice = wallpapers.slice(0, limit)
  await Promise.all(
    slice.map(
      wp =>
        new Promise<void>(resolve => {
          if (!wp?.id) return resolve()
          if (aspectCache.has(wp.id)) return resolve()
          const url = (wp as any).thumbnailUrl || wp.imageUrl
          if (!url) return resolve()
          const img = new Image()
          // Best effort only; ignore CORS failures silently
          img.onload = () => {
            if (img.naturalWidth && img.naturalHeight) {
              const ratio = img.naturalWidth / img.naturalHeight
              aspectCache.set(wp.id, ratio)
            }
            resolve()
          }
          img.onerror = () => resolve()
          img.decoding = 'async'
          img.src = url
        })
    )
  )
}

export const getWallpaperAspectRatio = (wp: Wallpaper): number | null => {
  // Prefer runtime measured aspect ratio if available
  if (wp?.id && aspectCache.has(wp.id)) return aspectCache.get(wp.id) || null
  const best = getBestResolution(wp.resolutions)
  if (best) return best.width / best.height
  // Fallback: try to parse from size string '1920x1080'
  const size = wp.resolutions?.[0]?.size
  if (size && /x/.test(size)) {
    const [w, h] = size.split('x').map(Number)
    if (w && h) return w / h
  }
  return null
}

// Simple device grouping for wallpaper filtering
const PHONE_GROUP: DeviceType[] = [
  DeviceType.MOBILE_PHONE,
  DeviceType.IPHONE,
  DeviceType.IPHONE_PRO,
  DeviceType.ANDROID
]

// SIMPLE APPROACH: Search for device keywords directly in the wallpaper object
const getAllLabeledDevices = (wp: Wallpaper): Set<DeviceType> => {
  const labeled = new Set<DeviceType>()
  
  // Convert entire wallpaper object to string and search for device keywords
  const wpString = JSON.stringify(wp).toLowerCase()
  
  // Search for device-specific keywords in the entire object
  if (wpString.includes('mobile') || wpString.includes('phone')) {
    labeled.add(DeviceType.MOBILE_PHONE)
  }
  if (wpString.includes('iphone')) {
    labeled.add(DeviceType.IPHONE)
  }
  if (wpString.includes('android')) {
    labeled.add(DeviceType.ANDROID)
  }
  if (wpString.includes('tablet')) {
    labeled.add(DeviceType.TABLET)
  }
  if (wpString.includes('laptop')) {
    labeled.add(DeviceType.LAPTOP)
  }
  if (wpString.includes('desktop')) {
    labeled.add(DeviceType.DESKTOP)
  }
  if (wpString.includes('ultrawide')) {
    labeled.add(DeviceType.ULTRAWIDE)
  }
  if (wpString.includes('tv')) {
    labeled.add(DeviceType.TV)
  }
  if (wpString.includes('watch')) {
    labeled.add(DeviceType.WATCH)
  }
  
  // Fallback: Check explicit fields if no keywords found
  if (labeled.size === 0) {
    // Check primaryDevice
    if (wp.primaryDevice) {
      labeled.add(wp.primaryDevice)
    }
    
    // Check deviceVariations keys
    if (wp.deviceVariations) {
      Object.keys(wp.deviceVariations).forEach(k => labeled.add(k as DeviceType))
    }
    
    // Check deviceVariationsList
    if (Array.isArray(wp.deviceVariationsList)) {
      wp.deviceVariationsList.forEach(entry => labeled.add(entry.device))
    }
    
    // Check supportedDevices
    if (Array.isArray(wp.supportedDevices)) {
      wp.supportedDevices.forEach(d => labeled.add(d))
    }
  }
  
  return labeled
}

export const isWallpaperLabeledForDevice = (wp: Wallpaper, device: DeviceType): boolean => {
  const wpAny = wp as any
  
  // Priority 1: Check explicit device labeling fields first
  const hasExplicitLabeling = checkExplicitDeviceLabeling(wp, device)
  if (hasExplicitLabeling !== null) {
    return hasExplicitLabeling
  }
  
  // Priority 2: STRICT RULE - If primaryDevice is set and conflicts with target device, reject immediately
  if (wpAny.primaryDevice) {
    // Mobile-primary wallpapers should NOT appear on laptop/desktop/ultrawide
    if (wpAny.primaryDevice === 'mobile_phone' && 
        (device === DeviceType.LAPTOP || device === DeviceType.DESKTOP || device === DeviceType.ULTRAWIDE)) {
      return false
    }
    // Desktop-primary wallpapers should NOT appear on mobile devices
    if (wpAny.primaryDevice === 'desktop' && PHONE_GROUP.includes(device)) {
      return false
    }
    // If primaryDevice matches exactly, allow it
    if (wpAny.primaryDevice === device) {
      return true
    }
  }
  
  // Priority 3: KEYWORD-BASED FILTERING - Only show if device keywords are found
  const wpString = JSON.stringify(wp).toLowerCase()
  const hasKeyword = (keyword: string) => wpString.includes(keyword)
  
  // For mobile devices: must contain mobile/phone keywords
  if (PHONE_GROUP.includes(device)) {
    return hasKeyword('mobile') || hasKeyword('phone') || hasKeyword('iphone') || hasKeyword('android')
  }
  
  // For tablet: must contain tablet keyword OR mobile keywords (tablets can use mobile wallpapers)
  if (device === DeviceType.TABLET) {
    return hasKeyword('tablet') || hasKeyword('mobile') || hasKeyword('phone')
  }
  
  // For laptop: must contain laptop keyword AND not be mobile-primary
  if (device === DeviceType.LAPTOP) {
    return hasKeyword('laptop') && wpAny.primaryDevice !== 'mobile_phone'
  }
  
  // For desktop: must contain desktop keyword OR laptop keyword (laptop/desktop sharing)
  if (device === DeviceType.DESKTOP) {
    return (hasKeyword('desktop') || hasKeyword('laptop')) && wpAny.primaryDevice !== 'mobile_phone'
  }
  
  // For ultrawide: must contain ultrawide keyword
  if (device === DeviceType.ULTRAWIDE) {
    return hasKeyword('ultrawide')
  }
  
  // For TV: must contain tv keyword
  if (device === DeviceType.TV) {
    return hasKeyword('tv')
  }
  
  // For watch: must contain watch keyword
  if (device === DeviceType.WATCH) {
    return hasKeyword('watch')
  }
  
  return false
}

// Helper function to check explicit device labeling fields
const checkExplicitDeviceLabeling = (wp: Wallpaper, device: DeviceType): boolean | null => {
  const wpAny = wp as any
  
  // Check deviceVariations - if device has a specific variation, it's explicitly supported
  if (wpAny.deviceVariations && wpAny.deviceVariations[device]) {
    return true
  }
  
  // Check deviceVariationsList - if device is in the list, it's explicitly supported
  if (Array.isArray(wpAny.deviceVariationsList)) {
    const hasDeviceVariation = wpAny.deviceVariationsList.some((entry: any) => 
      entry.device === device && entry.variations && entry.variations.length > 0
    )
    if (hasDeviceVariation) {
      return true
    }
  }
  
  // Check supportedDevices array
  if (Array.isArray(wpAny.supportedDevices) && wpAny.supportedDevices.includes(device)) {
    return true
  }
  
  // If no explicit labeling found, return null to continue with other checks
  return null
}

// Score how good a wallpaper fits a device
export const scoreWallpaperForDevice = (device: DeviceType, wp: Wallpaper): number => {
  // First check if wallpaper is labeled for this device
  if (!isWallpaperLabeledForDevice(wp, device)) {
    return 0
  }
  
  // Base score for being compatible
  let score = 1.0
  
  // Get all device labels for this wallpaper
  const deviceLabels = Array.from(getAllLabeledDevices(wp))
  
  // Bonus for primary device match (most important factor)
  const wpAny = wp as any
  if (wpAny.primaryDevice === device) {
    score += 10
  }
  
  // Bonus for direct device label match
  if (deviceLabels.includes(device)) {
    score += 5
  }
  
  // STRICT PENALTIES: Heavy penalty for cross-device contamination
  if (wpAny.primaryDevice === 'mobile_phone' && !PHONE_GROUP.includes(device)) {
    score -= 8  // Increased penalty to push mobile-primary wallpapers down
  }
  
  // Penalty for desktop-primary wallpapers on mobile devices
  if (wpAny.primaryDevice === 'desktop' && PHONE_GROUP.includes(device)) {
    score -= 5
  }
  
  // Additional penalty if this wallpaper only has mobile labels but is being shown on non-mobile
  const onlyMobileLabels = deviceLabels.every(label => PHONE_GROUP.includes(label))
  if (onlyMobileLabels && !PHONE_GROUP.includes(device) && device !== DeviceType.TABLET) {
    score -= 10  // Strong penalty for mobile-only wallpapers on desktop devices
  }
  
  // Small bonus for popularity (downloads/likes)
  if (wpAny.downloads && wpAny.downloads > 100) {
    score += 0.5
  }
  if (wpAny.likes && wpAny.likes > 50) {
    score += 0.2
  }
  
  return Math.max(score, 0)  // Ensure score doesn't go negative
}

export const sortWallpapersForDevice = (device: DeviceType, wallpapers: Wallpaper[]): Wallpaper[] => {
  // Prefilter: drop items that definitely don't fit orientation rules
  const eligible = wallpapers.filter(wp => scoreWallpaperForDevice(device, wp) > 0)
  return eligible.sort((a, b) => scoreWallpaperForDevice(device, b) - scoreWallpaperForDevice(device, a))
}

export const pickBestWallpaperForDevice = (device: DeviceType, wallpapers: Wallpaper[]): Wallpaper | null => {
  const sorted = sortWallpapersForDevice(device, wallpapers)
  return sorted[0] || null
}

// Debug utility: produce ranked list with scoring breakdown
export interface RankedWallpaperDebugInfo {
  id: string | undefined
  score: number
  labeled: boolean
  ratio: number | null
  primaryDevice?: DeviceType | null
  supportedDevices?: DeviceType[] | null
  hasDeviceVariation?: boolean
  hasDeviceVariationList?: boolean
}

export const rankWallpapersForDeviceDebug = (device: DeviceType, wallpapers: Wallpaper[], limit = 15): RankedWallpaperDebugInfo[] => {
  const rows: RankedWallpaperDebugInfo[] = wallpapers.map(wp => {
    const labeled = isWallpaperLabeledForDevice(wp, device)
    const ratio = getWallpaperAspectRatio(wp)
    const score = scoreWallpaperForDevice(device, wp)
    return {
      id: (wp as any).id,
      score,
      labeled,
      ratio,
      primaryDevice: (wp as any).primaryDevice || null,
      supportedDevices: Array.isArray((wp as any).supportedDevices) ? (wp as any).supportedDevices as DeviceType[] : null,
      hasDeviceVariation: !!(wp as any).deviceVariations && Object.keys((wp as any).deviceVariations || {}).length > 0,
      hasDeviceVariationList: Array.isArray((wp as any).deviceVariationsList) && (wp as any).deviceVariationsList.length > 0
    }
  })
  return rows
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
}

export const getDeviceTypeFromString = (str: string): DeviceType => {
  // Normalize common aliases/hyphenated names
  const normalized = (str || '').toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_')

  // Direct match
  const values = Object.values(DeviceType) as string[]
  if (values.includes(normalized)) return normalized as DeviceType

  // Map common aliases
  const aliasMap: Record<string, DeviceType> = {
    phone: DeviceType.MOBILE_PHONE,
    mobile: DeviceType.MOBILE_PHONE,
    mobile_phone: DeviceType.MOBILE_PHONE,
    'iphone-pro': DeviceType.IPHONE_PRO,
    iphone_pro: DeviceType.IPHONE_PRO
  }
  return aliasMap[normalized] ?? DeviceType.DESKTOP
}
